import {axios} from '@http_client';
import {$http} from "@http";
import cookie from 'react-cookies';
import {inArray} from "@helpers/arrays";
import {isEmpty} from "@helpers/helpers";
import {getFacilitiesPersian} from "@hooks/facilities";
import {getDensities} from "@hooks/density";
import {getFloorNames} from "@hooks/floors";
import {getDeeds} from "@hooks/deed";
import {getBusinessPermits} from "@hooks/business.permits";
import {getPositions} from "@hooks/positions";
import {is_array, objectIndexOf} from "../components/helpers/arrays";
import {str_replace} from "../components/helpers/helpers";
import {getCurrentTime} from "../components/helpers/dates";
import {getDefaultCity} from "../components/hooks/cities";
import {getFile} from "./media";
import {getCacheKey} from "./cache_key";

const cacheTable = 'properties';

class Property {
    constructor() {
        this.id = null;
        this.slug = "";
        this.user_id = null;
        this.agency_id = null;
        this.thumbnail = null;
        this.media = [];
        this.title = null;
        this.type = null;
        this.villa_type = null;
        this.deal_type = null;
        this.city_name = null;
        this.city_id = null;
        this.district_id = null;
        this.district_name = null;
        this.street = null;
        this.location = {
            latitude: null,
            longitude: null
        };
        this.features = {
            bedroom_count: null,
            age: null,
            deed: null,
            land_area: null,
        };
        this.facilities = [];
        this.mortgage = null;
        this.rent = null;
        this.guarantee = null;
        this.exchangeable = null;
        this.price_per_meter = null;
        this.loan = null;
        this.prepayment = null;
        this.installment = null;
        this.price = null;
        this.hide_price = false;
        this.land_area = null;
        this.infrastructure_area = null;
        this.is_empty = null;
        this.negotiable = false;
        this.dengue = null;
        this.unit_count = null;
        this.business_permit = null;
        this.length = null;
        this.height = null;
        this.position = null;
        this.boosted_at = null;
        this.boosted_at_jalali = null;
        this.registered_by = null;
        this.stage = null;
        this.status = null;
        this.description = null;
        this.private_description = null;
        this.created_at = null;
        this.created_at_jalali = null;
        this.archived = false;
        this.short_link = false;
        this.show_map = true;
        this.exists_in_kanban = false;
        this.request_contact = false;
        this.visit_count = 0;
        this.quick_sale_status = 'default';
        this.quick_sale_requested_at = null;

        this.contact_info = {
            agency_authenticated: false,
            agency_city_name: null,
            agency_district_name: null,
            agency_id: null,
            agency_name: null,
            agency_phone1: null,
            agency_slug: null,
            agency_username: null,
            avatar: null,
            id: null,
            name: null,
            phone1: null,
            realtor_city_name: null,
            realtor_district_name: null,
            realtor_id: null,
            realtor_name: null,
            realtor_phone1: null,
            realtor_slug: null,
            stars: null,
            stars_count: 0,
        }
    }

    /**
     *
     * @param {object} entity
     *
     * @return {Property}
     */
    static make(entity) {
        const property = new Property();

        property.id = entity.id ?? null;
        property.slug = entity.slug ?? null;
        property.user_id = entity.user_id ?? null;
        property.agency_id = entity.agency_id ?? null;
        property.thumbnail = entity.thumbnail ?? null;
        property.media = entity.media ?? [];
        property.title = entity.title ?? null;
        property.type = entity.type ?? null;
        property.villa_type = entity.villa_type ?? null;
        property.deal_type = entity.deal_type ?? null;
        property.city_name = entity.city_name ?? null;
        property.city_id = entity.city_id ?? null;
        property.district_id = entity.district_id ?? null;
        property.district_name = entity.district_name ?? null;
        property.street = entity.street ?? null;
        property.location = {
            latitude: (!isEmpty(entity.location) && !isEmpty(entity.location.latitude)) ? entity.location.latitude : null,
            longitude: (!isEmpty(entity.location) && !isEmpty(entity.location.longitude)) ? entity.location.longitude : null,
        };
        property.features = entity.features ?? {};
        property.facilities = entity.facilities ?? [];
        property.mortgage = entity.mortgage ?? null;
        property.rent = entity.rent ?? null;
        property.guarantee = entity.guarantee ?? null;
        property.exchangeable = entity.exchangeable ?? null;
        property.price_per_meter = entity.price_per_meter ?? null;
        property.loan = entity.loan ?? null;
        property.prepayment = entity.prepayment ?? null;
        property.installment = entity.installment ?? null;
        property.price = entity.price ?? null;
        property.hide_price = entity.hide_price ?? false;
        property.land_area = entity.land_area ?? null;
        property.infrastructure_area = entity.infrastructure_area ?? null;
        property.is_empty = entity.is_empty ?? null;
        property.negotiable = entity.negotiable ?? false;
        property.dengue = entity.dengue ?? null;
        property.unit_count = entity.unit_count ?? null;
        property.business_permit = entity.business_permit ?? null;
        property.length = entity.length ?? null;
        property.height = entity.height ?? null;
        property.position = entity.position ?? null;
        property.boosted_at = entity.boosted_at ?? null;
        property.boosted_at_jalali = entity.boosted_at_jalali ?? null;
        property.registered_by = entity.registered_by ?? null;
        property.stage = entity.stage ?? null;
        property.status = entity.status ?? null;
        property.description = entity.description ?? null;
        property.private_description = entity.private_description ?? null;
        property.created_at = entity.created_at ?? null;
        property.created_at_jalali = entity.created_at_jalali ?? null;
        property.archived = entity.archived ?? false;
        property.short_link = entity.short_link ?? null;
        property.show_map = entity.show_map ?? true;
        property.exists_in_kanban = entity.exists_in_kanban ?? false;
        property.request_contact = entity.request_contact ?? false;
        property.quick_sale_status = entity.quick_sale_status ?? 'default';
        property.quick_sale_requested_at = entity.quick_sale_requested_at ?? null;

        if (!isEmpty(entity.contact_info)) {
            property.contact_info = {
                agency_authenticated: entity.contact_info.agency_authenticated ?? false,
                agency_city_name: entity.contact_info.agency_city_name ?? null,
                agency_district_name: entity.contact_info.agency_district_name ?? null,
                agency_id: entity.contact_info.agency_id ?? null,
                agency_name: entity.contact_info.agency_name ?? null,
                agency_phone1: entity.contact_info.agency_phone1 ?? null,
                agency_slug: entity.contact_info.agency_slug ?? null,
                agency_username: entity.contact_info.agency_username ?? null,
                avatar: entity.contact_info.avatar ?? null,
                id: entity.contact_info.id ?? null,
                name: entity.contact_info.name ?? null,
                phone1: entity.contact_info.phone1 ?? null,
                realtor_city_name: entity.contact_info.realtor_city_name ?? null,
                realtor_district_name: entity.contact_info.realtor_district_name ?? null,
                realtor_id: entity.contact_info.realtor_id ?? null,
                realtor_name: entity.contact_info.realtor_name ?? null,
                realtor_phone1: entity.contact_info.realtor_phone1 ?? null,
                realtor_slug: entity.contact_info.realtor_slug ?? null,
                stars: entity.contact_info.stars ?? null,
                stars_count: entity.contact_info.stars_count ?? 0,
            };
        } else {
            property.contact_info = null;
        }

        return property;
    }
}

const $dealTypes = {
    'sale': 'فروش',
    'rent': 'رهن و اجاره',
    'mortgage': 'رهن کامل',
    'pre_sale': 'پیش فروش',
    'exchange': 'معاوضه',
    'participation': 'مشارکت',
};

const $searchDealTypes = {
    'sale': 'خرید',
    'rent': 'رهن و اجاره',
    'mortgage': 'رهن کامل',
    'pre_sale': 'پیش خرید',
    'exchange': 'معاوضه',
    'participation': 'مشارکت',
};

const $searchDealTypesNames = {
    'sale': 'خرید',
    'rent': 'اجاره',
    'mortgage': 'رهن کامل',
    'pre_sale': 'پیش خرید',
    'exchange': 'معاوضه',
    'participation': 'مشارکت',
};

const $searchiDealTypes = {
    'sale': 'فروشی',
    'rent': 'اجاره ای',
    'mortgage': 'رهنی',
    'pre_sale': 'پیش فروش',
    'exchange': 'معاوضه ای',
    'participation': 'مشارکتی برای ساخت',
};

const $urlDealTypes = {
    'pre_sale': 'پیش-خرید',
    'sale': 'خرید-فروش',
    'rent': 'رهن-اجاره',
    'mortgage': 'رهن-کامل',
    'exchange': 'معاوضه',
    'participation': 'مشارکت',
};

const $dealTypeValues = {
    '1': 'sale',
    '2': 'rent',
    '4': 'mortgage',
    '8': 'pre_sale',
    '16': 'exchange',
    '32': 'participation',
};

const $types = {
    'apartment': 'آپارتمان',
    'house': 'خانه ویلایی',
    'land': 'زمین',
    'villa': 'ویلا',
    'commercial': 'مغازه و تجاری',
    'suite': 'سوئیت',
    'office': 'دفتر کار اداری',
    'garden': 'باغ',
    'residential_complex': 'مجتمع مسکونی',
    'hotel': 'هتل',
    'apartment_hotel': 'هتل آپارتمان',
    'salon': 'سالن',
    'warehouse': 'انبار',
    'workhouse': 'کارگاه',
    'factory': 'کارخانه',
    'old_house': 'کلنگی',
    'realestate': 'مستغلات',
};

const $urlTypes = {
    'apartment_hotel': 'هتل-آپارتمان',
    'apartment': 'آپارتمان',
    'house': 'خانه-ویلایی',
    'commercial': 'مغازه-تجاری',
    'land': 'زمین',
    'office': 'دفتر-کار-اداری',
    'villa': 'ویلا',
    'garden': 'باغ',
    'suite': 'سوئیت',
    'residential_complex': 'مجتمع-مسکونی',
    'hotel': 'هتل',
    'salon': 'سالن',
    'warehouse': 'انبار',
    'workhouse': 'کارگاه',
    'factory': 'کارخانه',
    'old_house': 'کلنگی',
    'realestate': 'مستغلات',
};

const $typeCaptionsByName = {
    'apartment_hotel': 'هتل آپارتمان',
    'apartment': 'آپارتمان',
    'house': 'خانه ویلایی',
    'commercial': 'مغازه تجاری',
    'land': 'زمین',
    'office': 'دفتر کار اداری',
    'villa': 'ویلا',
    'garden': 'باغ',
    'suite': 'سوئیت',
    'residential_complex': 'مجتمع مسکونی',
    'hotel': 'هتل',
    'salon': 'سالن',
    'warehouse': 'انبار',
    'workhouse': 'کارگاه',
    'factory': 'کارخانه',
    'old_house': 'کلنگی',
    'realestate': 'مستغلات',
};

const $typeValues = {
    '1': 'apartment',
    '2': 'house',
    '4': 'villa',
    '8': 'commercial',
    '16': 'office',
    '32': 'land',
    '128': 'workhouse',
    '256': 'salon',
    '512': 'apartment_hotel',
    '1024': 'suite',
    '2048': 'garden',
    '4096': 'residential_complex',
    '8192': 'hotel',
    '16384': 'warehouse',
    '32768': 'factory',
    '65536': 'کلنگی',
    '131072': 'مستغلات',
};

const $villaTypes = {
    'beach': 'ساحلی',
    'forest': 'جنگلی',
    'town': 'شهرکی',
};

/**
 * @param {int|string|null} $dealType
 *
 * @return {string}
 */
export function getUrlDealTypes($dealType = null) {
    if (isEmpty($dealType)) {
        return $urlDealTypes;
    } else if (typeof $dealType === "number") {
        if (!isEmpty($dealTypeValues[$dealType])) {
            return $urlDealTypes[$dealTypeValues[$dealType]];
        }
    } else if (!isEmpty($dealType)) {
        if (!isEmpty($urlDealTypes[$dealType])) {
            return $urlDealTypes[$dealType];
        }
    }
    return false;
}

/**
 * @param {int|string|null} $dealType
 *
 * @return {string}
 */
export function getPropertyDealTypes($dealType = null) {
    if (isEmpty($dealType)) {
        return $dealTypes;
    } else if (typeof $dealType === "number") {
        if (!isEmpty($dealTypeValues[$dealType])) {
            return $dealTypes[$dealTypeValues[$dealType]];
        }
    } else if (!isEmpty($dealType)) {
        if (!isEmpty($dealTypes[$dealType])) {
            return $dealTypes[$dealType];
        }
    }
    return false;
}

/**
 * @param {int|string|null} dealType
 *
 * @return {string}
 */
export function getSearchiDealTypes(dealType = null) {
    if (isEmpty(dealType)) {
        return $searchDealTypes;
    } else {
        return $searchDealTypes[dealType];
    }
}

/**
 * @param {int|string|null} $key
 * @param {boolean|null} $getAll
 *
 * @return {Object|string|boolean}
 */
export function getDealTypeValues($key = null, $getAll = false) {

    if (isEmpty($key)) {
        return $dealTypeValues;
    } else if (typeof $key === "number") {
        if (!isEmpty($dealTypeValues[$key])) {
            return $dealTypes[$dealTypeValues[$key]];
        } else if ($getAll) {
            let $dealTypes = [];
            for (let $dealTypeKey in $dealTypeValues) {
                const $value = $dealTypeValues[$dealTypeKey];
                if (($dealTypeKey & $key) > 0) {
                    $dealTypes.push($value);
                }
            }
            return $dealTypes;
        }
    } else if (objectIndexOf($key, $dealTypeValues)) {
        return $dealTypeValues[objectIndexOf($key, $dealTypeValues)];
    } else if (typeof $key === "object") {
        let $dealTypeBits = 0;
        for (let item in $key) {
            const itemValue = $key[item];
            if (objectIndexOf(itemValue, $dealTypeValues)) {
                $dealTypeBits |= item;
            }
        }
        return $dealTypeBits;
    }
    return false;
}

/**
 * @param {string|null} deal_type
 *
 * @return {string|Object}
 */
export function getDealTypeSearchName(deal_type = null) {
    if (isEmpty(deal_type)) {
        return $searchDealTypesNames;
    } else {
        return $searchDealTypesNames[deal_type];
    }
}

/**
 * @param {int|string|null} $type
 *
 * @return {string}
 */
export function getUrlTypes($type = null) {
    const $types = $urlTypes;

    if (isEmpty($type)) {
        return $types;
    } else if (typeof $type === "number") {
        if (!isEmpty($typeValues[$type])) {
            return $urlTypes[$typeValues[$type]];
        }
    } else if (!isEmpty($type)) {
        if (!isEmpty($types[$type])) {
            return $types[$type];
        } else if (typeof $type === "string" && !$types.hasOwnProperty($type)) {
            const customTypeName = $type.split('.')[0];

            return $types[customTypeName];
        }
    }

    return false;
}


/**
 * @param {string|null} typeKey
 * @param {number|null} city_id
 *
 * @return {string|Object}
 */
export async function getTypeCaptionByName(typeKey = null, city_id = null) {
    if (isEmpty(typeKey) && isEmpty(city_id)) {
        return $typeCaptionsByName;
    } else if (!isEmpty(city_id)) {
        const {types} = await getPropertyTypesByCityId(city_id);

        if (!isEmpty(typeKey)) {
            let typesTitle = '';

            for (let type of types) {
                if (type.name === typeKey) {
                    return (!isEmpty(type.caption) ? type.caption : type.title);
                } else if (String(type.name).indexOf('.') !== -1) {
                    const customTypeName = type.name.split('.');

                    if (customTypeName.length) {
                        for (let item of customTypeName) {
                            if (typeKey === item) {
                                typesTitle += (!isEmpty(type.caption) ? type.caption : type.title) + ' '
                            }
                        }
                    }
                }
            }

            return typesTitle;
        } else {
            return types;
        }
    } else {
        return $typeCaptionsByName[typeKey];
    }
}

/**
 * @param {int,|null} $city_id
 *
 * @return {string}
 */
export async function getUrlTypesByCityId($city_id = getDefaultCity()) {
    let $types = {};

    if (!isEmpty($city_id)) {
        let {types} = await getPropertyTypesByCityId($city_id);

        for (let type of types) {
            $types[type.name] = str_replace(' ', '-', type.title)
        }
    }

    return $types;
}

/**
 * @param {int|string|null} $type
 *
 * @return {string}
 */
export function getPropertyTypes($type = null) {

    if (isEmpty($type)) {
        return $types;
    } else if (typeof $type === "number") {
        if (!isEmpty($typeValues[$type])) {
            return $types[$typeValues[$type]];
        }
    } else if (is_array($type)) {
        let $result = [];

        Object.keys($type).forEach(value => {
            const type = $type[value];

            if (!isEmpty($typeCaptionsByName[type])) {
                $result[value] = $typeCaptionsByName[type];
            }
        });

        return $result;
    } else if (!isEmpty($type)) {
        if (!isEmpty($types[$type])) {
            return $types[$type];
        }
    }

    return false;
}

export function getTypeValues($key = null, $getAll = false, $city_id = null) {
    let $typeValues = $typeValues;

    // TODO:: merged types by city_id

    if (isEmpty($key)) {
        return $types;
    } else if (typeof $key === "number") {
        if (!isEmpty($typeValues[$key])) {
            return $typeValues[$key];
        } else {
            let $result = [];

            Object.keys($typeValues).forEach(value => {
                const type = $typeValues[value];

                if ((Number(value) & Number($key)) > 0) {
                    $result[value] = type;
                }
            });

            return $result;
        }
    } else if (typeof $key === "string") {
        if (objectIndexOf($key, $typeValues)) {
            return $typeValues[objectIndexOf($key, $typeValues)];
        } else {
            // TODO:: $mergedTypes
        }
    } else if (is_array($key)) {
        let $typeBits = 0;

        Object.keys($key).forEach(value => {
            const item = $key[value];

            if (objectIndexOf(item, $typeValues)) {
                $typeBits |= objectIndexOf(item, $typeValues);
            }
        });

        return $typeBits;
    }

    return false;
}

export function getVillaTypes($key = null) {
    if (!isEmpty($key) && !isEmpty($villaTypes[$key])) {
        return $villaTypes[$key];
    }

    return $villaTypes;
}

function getPropertyDetails(property, max) {
    const {type, deal_type, features} = property;
    let importantDetails = new Set();

    if (inArray(type, ['apartment', 'house', 'villa', 'commercial', 'office', 'hotel', 'apartment_hotel', 'residential_complex'])) {
        importantDetails.add('bedroom_count');
    }

    if (inArray(type, ['apartment', 'commercial', 'office', 'apartment_hotel', 'residential_complex'])) {
        importantDetails.add('floor');
    }

    if (inArray(deal_type, ['sale', 'exchange']) && inArray(type, ['apartment', 'house', 'villa', 'commercial', 'office', 'hotel', 'apartment_hotel', 'residential_complex', 'suite'])) {
        importantDetails.add('age');
    }

    importantDetails.add('facilities');

    if (inArray(deal_type, ['sale', 'pre_sale', 'exchange', 'participation'])) {
        importantDetails.add('deed');
    }

    if (inArray(type, ['house', 'commercial', 'land', 'apartment_hotel', 'residential_complex', 'hotel'])) {
        importantDetails.add('density');
    }

    if (inArray(type, ['garden'])) {
        importantDetails.add('land_area');
    }

    if (inArray(type, ['commercial', 'office', 'land', 'salon', 'warehouse', 'workhouse', 'factory'])) {
        importantDetails.add('business_permit');
    }

    if (inArray(type, ['commercial'])) {
        importantDetails.add('height');
    }

    if (inArray(type, ['commercial', 'land'])) {
        importantDetails.add('length');
    }

    if (inArray(type, ['apartment', 'house', 'villa', 'commercial', 'office', 'land', 'hotel', 'apartment_hotel', 'residential_complex', 'suite'])) {
        importantDetails.add('position');
    }

    // get data
    let data = new Map();
    let details = {};
    if (!isEmpty(property.details)) {
        details = property.details;
    }

    for (let field of importantDetails) {

        switch (field) {
            case 'floor' :
                if (!isEmpty(features) && !isEmpty(features.floor)) {
                    let counter = 0;
                    let isAllFloors = false;

                    if (is_array(features.floor)) {
                        for (let floor of features.floor) {
                            if (!inArray(floor, ['-1', '0'])) {
                                counter += 1;
                            }
                        }
                    } else if (features.floor === "-3" && features.floor_count > 1) {
                        isAllFloors = true;
                    }

                    const floors = getFloorNames(features.floor);

                    if (!isAllFloors && !isEmpty(floors) && (isEmpty(features.floor_count) || counter < features.floor_count)) {
                        if (!isEmpty(floors)) {
                            const floor_text = 'طبقه ' + floors.join('، ');
                            data.set(field, str_replace('36', '35+', floor_text));
                        }
                    } else if (!isEmpty(features.floor_count) && (counter === features.floor_count || isAllFloors)) {
                        data.set(field, 'همه ' + features.floor_count + ' طبقه');
                    }
                }
                break;
            case 'bedroom_count':
                if (!isEmpty(features) && !isEmpty(features.bedroom_count)) {
                    data.set(field, features.bedroom_count + (inArray(type, ['apartment', 'house', 'villa', 'garden', 'suite', 'hotel', 'apartment_hotel', 'residential_complex']) ? ' خوابه' : ' اتاقه'));
                }
                break;
            case 'age' :
                if (!isEmpty(features) && !isEmpty(features.age)) {
                    let age = features.age + ' سال ساخت';
                    if (features.age === 0) {
                        age = 'نوساز';
                    } else if (features.age === 30) {
                        age = 'بالای 30 سال ساخت';
                    }

                    data.set(field, age);
                }
                break;
            case 'facilities':
                if (!isEmpty(features) && !isEmpty(property.facilities) && inArray('elevator', property.facilities)) {
                    const facility = getFacilitiesPersian('elevator');
                    if (!isEmpty(facility)) {
                        data.set(field, facility);
                    }
                }
                break;
            case 'density':
                if (!isEmpty(features) && !isEmpty(features.density)) {
                    data.set(field, 'تراکم ' + getDensities(features.density));
                }
                break;
            case 'floor_count':
                if (!isEmpty(features) && !isEmpty(features.floor_count)) {
                    data.set(field, features.floor_count + ' طبقه');
                }
                break;
            case 'land_area':
                if (!isEmpty(features) && !isEmpty(property.land_area)) {
                    data.set(field, 'زمین ' + property.land_area + ' متری');
                }
                break;
            case 'deed':
                let deeds = '';
                if (!isEmpty(features) && !isEmpty(features.deed)) {
                    deeds = 'سند ' + getDeeds(features.deed)
                }

                if (!isEmpty(deeds)) {
                    data.set(field, deeds);
                }
                break;
            case 'unit_count':
                if (!isEmpty(details) && !isEmpty(details.unit_count)) {
                    data.set(field, details.unit_count + ' واحدی');
                }
                break;
            case 'business_permit':
                if (!isEmpty(details) && !isEmpty(details.business_permit)) {
                    data.set(field, 'مجوز تجاری ' + getBusinessPermits(details.business_permit));
                }
                break;
            case 'length':
                if (!isEmpty(details) && !isEmpty(details.length) && details.length > 0) {
                    data.set(field, str_replace('.', '٫', details.length) + ' متر طول بر');
                }
                break;
            case 'height':
                if (!isEmpty(details) && !isEmpty(details.height) && details.height > 0) {
                    data.set(field, str_replace('.', '٫', details.height) + ' متر ارتفاع');
                }
                break;
            case 'position':
                if (!isEmpty(details) && !isEmpty(details.position)) {
                    data.set(field, getPositions(details.position));
                }
                break;
        }

        if (max > 0 && data.size === max) {
            break;
        }
    }

    return data;
}


// ******************************  API **************************


/**
 * @param city_id
 *
 * @return Promise {types}
 * */
function getPropertyTypesByCityId(city_id) {
    const path = '/v1/properties/types/' + city_id;
    const cacheKey = getCacheKey(cacheTable, 'propertyTypes', 'city', [city_id]);
    const cacheTTL = 24 * 60 * 60; // second


    return new Promise((resolve, reject) => {
        let localTypes = null;

        if (process.browser) {
            localTypes = localStorage.getItem('property_types');

            if (!isEmpty(localTypes)) {
                localTypes = JSON.parse(localTypes);
            }
        }

        if (!isEmpty(localTypes) && Number(localTypes.city_id) === Number(city_id)) {
            resolve(localTypes.lists);
        } else {

            $http(cacheKey, path, cacheTTL).then(result => {

                if (process.browser) {
                    const storeData = {
                        city_id: city_id,
                        lists: result,
                        expire: getCurrentTime(24 * 60 * 60) // 1 day
                    };

                    localStorage.setItem('property_types', JSON.stringify(storeData));
                }

                resolve(result)
            }).catch(err => {
                reject(err)
            })
        }
    })
}

/**
 * @param city_id
 *
 * @return Promise {types}
 * */
function getPropertyDealTypesByCityId(city_id) {
    const path = '/v1/properties/dealTypes/' + city_id;
    const cacheKey = getCacheKey(cacheTable, 'propertyDealTypes', 'city', [city_id]);
    const cacheTTL = 24 * 60 * 60; // second


    return new Promise((resolve, reject) => {
        let localDealTypes = null;

        if (process.browser) {
            localDealTypes = localStorage.getItem('property_deal_types');
            if (!isEmpty(localDealTypes)) {
                localDealTypes = JSON.parse(localDealTypes);
            }
        }

        if (!isEmpty(localDealTypes) && Number(localDealTypes.city_id) === Number(city_id)) {
            resolve(localDealTypes.lists);
        } else {

            $http(cacheKey, path, cacheTTL).then(result => {

                if (process.browser) {
                    const storeData = {
                        city_id: city_id,
                        lists: result,
                        expire: getCurrentTime(24 * 60 * 60) // 1 day
                    };

                    localStorage.setItem('property_deal_types', JSON.stringify(storeData));
                }

                resolve(result)
            }).catch(err => {
                reject(err)
            })
        }
    })
}

/**
 * @param {int} property_id
 *
 * @return {Promise<Property>}
 */
function getProperty(property_id) {
    const path = '/v1/properties/' + property_id + '/get';
    const cacheKey = null; // not cache  //getCacheKey(cacheTable, 'show', 'id', [property_id]);
    const cacheTTL = 5 * 60; // second


    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(Property.make(result.property));
        }).catch(error => {
            reject(error)
        })
    });
}

/**
 * @return {Promise<Property>}
 */
function getUserProperties() {
    const path = '/v1/properties/myProperties';

    return new Promise((resolve, reject) => {
        axios.get(path).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error)
        })
    });
}

/**
 * @return {Promise<Property>}
 */
function getBookmarkProperties() {
    const path = '/v1/properties/bookmarks';

    return new Promise((resolve, reject) => {
        axios.get(path).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error)
        })
    });
}

/**
 * @param {number} property_id
 * @param {Object} media
 * @param {number} boosted_at
 *
 * @return string
 */
function getImagePath(property_id = null, media = null, boosted_at = null) {
    if (!isEmpty(property_id) && !isEmpty(media) && !isEmpty(media.file_name)) {
        return getFile('/properties/' + property_id + '/' + media.file_name + '?' + boosted_at);
    } else {
        return '/_img/default/property.jpg';
    }
}

/**
 * @param {Object} property
 * @param size {Object|nullable} => {w:260, h:185}
 * @param media {Object|nullable}
 * @param isGridList {Boolean}
 *
 * @return string
 */
function getThumbnailPath(property = null, size = null, media = null, isGridList = false) {
    if (!isEmpty(property) && !property.archived && !isEmpty(property.thumbnail)) {
        const file_name = !isEmpty(media) ? str_replace('.jpg', '', media.file_name) : str_replace('.jpg', '', property.thumbnail.file_name)

        return getFile('/properties/' + property.id + '/' + file_name + '-thumb.jpg?' + property.boosted_at + (!isEmpty(size) ? ('&w=' + size.w + '&h=' + size.h) : ''));
    } else {
        let link = '/_img/default/property.jpg';

        if (isGridList) {
            link = '/_img/default/noImage.jpg';
        }

        return link;
    }
}

/**
 * @param {number} video_code
 *
 * @return string
 */
function getAparatVideoPath(video_code) {
    return 'https://www.aparat.com/video/video/embed/videohash/' + video_code + '/vt/frame'
}

/**
 * @param {int} property_id
 * @param {int} suggested_realtor_id => optional
 *
 * @return {Promise<getPropertyContactInfo>}
 */
function getPropertyContactInfo(property_id, suggested_realtor_id = null) {
    const path = 'v1/properties/' + property_id + '/contactInfo' + (suggested_realtor_id !== null ? '/' + suggested_realtor_id : '');

    return new Promise((resolve, reject) => {
        axios.get(path).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error)
        })
    })
}

/**
 * @param {property_id&type&description} param
 *
 * @return {Promise<data>}
 */
function reportProperty(param) {
    const path = '/v1/properties/report';

    return new Promise((resolve, reject) => {
        axios.post(path, param).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error.response.data);
        })
    });
}


/**
 * @param {number} property_id
 *
 * @return {Promise}
 */
function getSimilarProperties(property_id) {
    const path = '/v1/properties/' + property_id + '/similar';
    const cacheKey = getCacheKey(cacheTable, 'similarProperties', 'property', [property_id]);
    const cacheTTL = 10 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result);
        }).catch(error => {
            reject(error)
        })
    })
}

/**
 * @param user_id {Number}
 *
 * @return {Promise}
 */
function getBookmarkedPropertiesIds(user_id) {
    const path = '/v1/properties/bookmarks/onlyId';
    const cacheKey = ''; //
    const cacheTTL = 5 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(response => {
            resolve(response);
        }).catch(error => {
            reject(error)
        })
    })
}

/**
 * @param {number} property_id
 * @param {boolean} follow
 *
 * @return {Promise<boolean>}
 */
function handlePropertyFollow(property_id, follow) {
    const path = '/v1/properties/' + property_id + '/' + (follow ? 'follow' : 'unfollow');

    const param = {};

    // when run after login auth-token not set in axios
    const token = cookie.load('auth_token');
    if (token) {
        axios.defaults.headers.common['Auth-Token'] = token
    }

    return new Promise((resolve, reject) => {
        axios.post(path, param).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error);
        })
    });
}

/**
 * @param property_id {Number}
 *
 * @return Promise
 * */
function removeUserProperty(property_id) {
    const path = '/v1/properties/myProperty/' + property_id;

    return new Promise((resolve, reject) => {
        axios.delete(path).then(result => {
            resolve(result)
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param property_id {Number}
 *
 * @return Promise
 * */
export function storeQuickSaleProperty(property_id) {
    const path = `/v1/properties/myProperty/${property_id}/quick-sale`;

    return new Promise((resolve, reject) => {
        axios.post(path, {}).then(result => {
            resolve(result)
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param property_id {Number}
 *
 * @return Promise
 * */
function realtorsAroundProperty(property_id) {
    const path = '/v1/properties/' + property_id + '/realtorsAround';
    const cacheKey = '';
    const cacheTTL = 5 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result)
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param property_id {number}
 * @param name {string}
 * @param mobile {number}
 * @param captcha_code {number|N}
 * @param source {String|badesaba|2nabsh}
 * @param mode {String|default|contact}
 *
 * @return Promise
 * */

export function storePropertyCallRequest({property_id, name, mobile, captcha_code, source, mode = 'default'}) {
    if (!isEmpty(property_id)) {
        const path = '/v1/properties/' + property_id + '/callRequest';

        const data = {
            property_id: property_id,
            name: name,
            mobile: mobile,
            captcha_code: captcha_code,
            source: source,
            mode: mode
        };

        return new Promise((resolve, reject) => {
            axios.post(path, data).then(result => {
                resolve(result.data)
            }).catch(err => {
                reject(err);
            })
        })
    }
}

export default Property;
export {
    getPropertyDetails,
    getProperty,
    getUserProperties,
    getBookmarkProperties,
    reportProperty,
    getPropertyContactInfo,
    getAparatVideoPath,
    getImagePath,
    getThumbnailPath,
    handlePropertyFollow,
    getBookmarkedPropertiesIds,
    getSimilarProperties,
    getPropertyTypesByCityId,
    getPropertyDealTypesByCityId,
    removeUserProperty,
    realtorsAroundProperty
}